// ПОЛ В ТЕКСТАХ ОТ ЛИЦА ВЛАДЕЛЬЦА АККАУНТА (ONBOARDING-PLAN О5.4, владелец 03.09.2026).
//
// Зачем: часть реплик бот говорит не «от бота», а ОТ ЛИЦА хозяина аккаунта, и в русском
// прошедшее время выдаёт пол — «отправил» или «отправила». Пол аккаунта у нас есть
// (client_portraits, догадка модели плюс то, что человек подтвердил на карточке
// подключения), поэтому такие слова в текстах стоят не буквой, а МЕТКОЙ:
//
//     {{g:отправила|отправил|отправили}}
//      ^ женское    ^ мужское  ^ нейтральное (пола не знаем)
//
// Порядок частей ровно такой и менять его нельзя: он записан в каноне
// (docs/HOW-IT-WORKS.md §6) и в текстах шаблонов, которые уже лежат в базе.
//
// Метку раскрывает СБОРКА бота из шаблона (apps/web api drafts) и сборка копии сцены
// примеров (apps/web lib/niche-scene.ts) — по полу того аккаунта, который человек выбрал
// в мастере. Пола нет — берётся нейтральная форма; это законный ответ, а не ошибка
// (запрет №7: клиенту про «модель не определила» ничего не показываем).
//
// Синтаксис нарочно не похож на выражения n8n (`{{ $json.x }}`): после `{{` сразу идёт
// `g:` без пробела, поэтому формулы канона под раскрытие не попадают.
import type { PortraitGender } from './portrait';

/** Пол владельца аккаунта — тот же тип, что у портрета: реэкспорт, чтобы модуль был
 *  самодостаточным (его зовут и там, где остального портрета не нужно). */
export type { PortraitGender };

/** Три формы одного слова: женская, мужская и нейтральная («пола не знаем»). */
export interface GenderWordForms {
  female: string;
  male: string;
  /** Когда пол неизвестен: «отправили», «мы отправили». */
  neutral: string;
}

/** Форма слова по полу владельца аккаунта. Пола нет — нейтральная. Чистая — юниты. */
export function genderWord(
  gender: PortraitGender | null | undefined,
  forms: GenderWordForms,
): string {
  return gender === 'female' ? forms.female : gender === 'male' ? forms.male : forms.neutral;
}

/** Метка пола в тексте: `{{g:жен|муж|нейтр}}`. Части — без `{`, `}` и `|`. */
const GENDER_TAG = /\{\{g:([^|{}]*)\|([^|{}]*)\|([^|{}]*)\}\}/g;

/** Есть ли в строке метка пола (быстрая проверка до разбора). Чистая. */
export function hasGenderTag(text: string): boolean {
  return text.includes('{{g:') && new RegExp(GENDER_TAG.source).test(text);
}

/**
 * РАСКРЫТИЕ МЕТОК В СТРОКЕ. Меток нет — возвращается та же строка (и та же ссылка).
 * Чистая — юниты.
 */
export function expandGender(text: string, gender: PortraitGender | null | undefined): string {
  if (!text.includes('{{g:')) return text;
  return text.replace(GENDER_TAG, (_all, female: string, male: string, neutral: string) =>
    genderWord(gender, { female, male, neutral }),
  );
}

/**
 * РАСКРЫТИЕ МЕТОК ВО ВСЁМ JSON (канон бота, определение сцены). Иммутабельно (запрет №3):
 * новая структура строится только там, где что-то поменялось, — ничего не нашлось,
 * возвращается ТОТ ЖЕ объект. Это важно сцене: её копия сверяется по ссылке.
 * Не-JSON значения (функции, даты) не трогаются. Чистая — юниты.
 *
 * КЛЮЧИ ОБЪЕКТОВ раскрываются тоже, и это не украшение: в каноне n8n связи (`connections`)
 * лежат под ИМЕНАМИ узлов, и если раскрыть метку только в значениях, связь потеряет свой
 * узел и бот развалится. Обычные поля меток не несут — их раскрытие не трогает.
 */
export function expandGenderDeep<T>(value: T, gender: PortraitGender | null | undefined): T {
  if (typeof value === 'string') {
    return expandGender(value, gender) as unknown as T;
  }
  if (Array.isArray(value)) {
    let changed = false;
    const next = value.map((v) => {
      const one = expandGenderDeep(v, gender);
      if (one !== v) changed = true;
      return one;
    });
    return (changed ? next : value) as unknown as T;
  }
  // Только простые объекты: экземпляры классов (Date, Buffer) остаются как есть.
  if (value !== null && typeof value === 'object' && isPlainObject(value)) {
    let changed = false;
    const next: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
      const one = expandGenderDeep(v, gender);
      const key = expandGender(k, gender);
      if (one !== v || key !== k) changed = true;
      next[key] = one;
    }
    return (changed ? next : value) as unknown as T;
  }
  return value;
}

function isPlainObject(v: object): boolean {
  const proto = Object.getPrototypeOf(v) as unknown;
  return proto === Object.prototype || proto === null;
}
